import { useState, useEffect } from 'react'
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, getDocs, orderBy } from 'firebase/firestore'
import { db } from '../lib/firebase'
import AddSkillModal from '../components/AddSkillModal'
import SkillCard from '../components/SkillCard'
import { toast } from 'react-hot-toast'
import AddSubtopicModal from '../components/AddSubtopicModal'
import NotesModal from '../components/NotesModal'
import EditSubtopicModal from '../components/EditSubtopicModal'
import { Link } from 'react-router-dom'

// Define badge criteria
const badgeCriteria = [
  { id: 'subtopic-conqueror', type: 'completion', target: 'subtopic', count: 1, name: 'Subtopic Conqueror', description: 'Completed 1 subtopic.' },
  { id: 'subtopic-streak', type: 'completion', target: 'subtopic', count: 5, name: 'Subtopic Streak', description: 'Completed 5 subtopics.' },
  { id: 'skill-achiever', type: 'completion', target: 'skill', count: 1, name: 'Skill Achiever', description: 'Completed 1 skill.' },
  { id: 'resource-explorer', type: 'engagement', target: 'resource', count: 1, name: 'Resource Explorer', description: 'Added 1 learning resource.' },
  { id: 'note-taker', type: 'engagement', target: 'notes', count: 1, name: 'Note Taker', description: 'Added notes to 1 subtopic.' },
  // Add more badge criteria here
  { id: 'subtopic-master', type: 'completion', target: 'subtopic', count: 10, name: 'Subtopic Master', description: 'Completed 10 subtopics.' },
  { id: 'skill-enthusiast', type: 'completion', target: 'skill', count: 3, name: 'Skill Enthusiast', description: 'Completed 3 skills.' },
  { id: 'skill-mastermind', type: 'completion', target: 'skill', count: 5, name: 'Skill Mastermind', description: 'Completed 5 skills.' },
  { id: 'resource-curator', type: 'engagement', target: 'resource', count: 5, name: 'Resource Curator', description: 'Added 5 learning resources.' },
  { id: 'knowledge-linker', type: 'engagement', target: 'resource', count: 10, name: 'Knowledge Linker', description: 'Added 10 learning resources.' },
  { id: 'detailed-notetaker', type: 'engagement', target: 'notes', count: 3, name: 'Detailed Notetaker', description: 'Added notes to 3 subtopics.' },
  { id: 'knowledge-documenter', type: 'engagement', target: 'notes', count: 5, name: 'Knowledge Documenter', description: 'Added notes to 5 subtopics.' },
];

export default function Dashboard({ user }) {
  const [skills, setSkills] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingSkill, setEditingSkill] = useState(null)
  const [showAddSubtopicModal, setShowAddSubtopicModal] = useState(false)
  const [selectedSkillForSubtopic, setSelectedSkillForSubtopic] = useState(null)

  // State for NotesModal
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedSkillForNotes, setSelectedSkillForNotes] = useState(null);
  const [selectedSubtopicForNotes, setSelectedSubtopicForNotes] = useState(null);

  // State for EditSubtopicModal
  const [showEditSubtopicModal, setShowEditSubtopicModal] = useState(false);
  const [selectedSkillForEdit, setSelectedSkillForEdit] = useState(null);
  const [selectedSubtopicForEdit, setSelectedSubtopicForEdit] = useState(null);

  const [userBadges, setUserBadges] = useState([]); // State to hold user's earned badges
  const [userData, setUserData] = useState(null); // State to hold full user data

  // Fetch skills and user data
  useEffect(() => {
    if (!user?.uid) return;

    // Subscribe to skills collection
    const skillsQuery = query(collection(db, 'skills'), where('userId', '==', user.uid))
    const unsubscribeSkills = onSnapshot(skillsQuery, (snapshot) => {
      const skillsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setSkills(skillsData)
    })

    // Subscribe to user document
    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribeUser = onSnapshot(userDocRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
            setUserData(docSnapshot.data());
            setUserBadges(docSnapshot.data().earnedBadges || []);
        } else {
            // Handle case where user doc doesn't exist yet (e.g., new user)
            setUserData({ earnedBadges: [] });
            setUserBadges([]);
        }
    });

    return () => {
        unsubscribeSkills();
        unsubscribeUser();
    };
  }, [user?.uid]); // Dependency on user.uid

  // Function to check and award badges
  const checkAndAwardBadges = async (currentSkills, currentUserData) => {
      if (!currentUserData || !user?.uid) return; // Ensure user data and id are available

    let updatedBadges = [...(currentUserData.earnedBadges || [])];
    
    // Recalculate counts based on current skills data for accuracy
    const completedSubtopicCount = currentSkills.reduce((count, skill) => 
        count + (skill.subtopics?.filter(st => st.status === 'completed').length || 0)
    , 0);
    const completedSkillCount = currentSkills.filter(skill => 
        skill.subtopics?.length > 0 && skill.subtopics.every(st => st.status === 'completed')
    ).length;
    const resourcesAddedCount = currentSkills.reduce((count, skill) => 
        count + (skill.subtopics?.reduce((subCount, st) => subCount + (st.resources?.length || 0), 0) || 0)
    , 0);
    const notesAddedCount = currentSkills.reduce((count, skill) => 
        count + (skill.subtopics?.filter(st => st.notes && st.notes.length > 0).length || 0)
    , 0);

    let countsChanged = 
        completedSubtopicCount !== (currentUserData.completedSubtopicCount || 0) ||
        completedSkillCount !== (currentUserData.completedSkillCount || 0) ||
        resourcesAddedCount !== (currentUserData.resourcesAddedCount || 0) ||
        notesAddedCount !== (currentUserData.notesAddedCount || 0);

    let newBadgesEarned = [];

    badgeCriteria.forEach(badge => {
      if (!updatedBadges.includes(badge.id)) {
        let earned = false;
        if (badge.type === 'completion') {
          if (badge.target === 'subtopic' && completedSubtopicCount >= badge.count) earned = true;
          if (badge.target === 'skill' && completedSkillCount >= badge.count) earned = true;
        } else if (badge.type === 'engagement') {
           if (badge.target === 'resource' && resourcesAddedCount >= badge.count) earned = true;
           if (badge.target === 'notes' && notesAddedCount >= badge.count) earned = true;
        }

        if (earned) {
          updatedBadges.push(badge.id);
          newBadgesEarned.push(badge);
        }
      }
    });

    // Update user document if badges or counts changed
    if (newBadgesEarned.length > 0 || countsChanged) {
        const userDocRef = doc(db, 'users', user.uid);
        try {
            await updateDoc(userDocRef, { 
                earnedBadges: updatedBadges, 
                completedSubtopicCount: completedSubtopicCount,
                completedSkillCount: completedSkillCount,
                resourcesAddedCount: resourcesAddedCount,
                notesAddedCount: notesAddedCount,
            });
            newBadgesEarned.forEach(badge => {
                toast.success(`Badge Earned: ${badge.name}! ${badge.description}`);
            });
        } catch (error) {
            console.error('Error updating user document with badges/counts:', error);
            toast.error('Failed to update user achievements.');
        }
    }
  };

  // Watch for changes in skills or user data to trigger badge checks
  useEffect(() => {
      if (skills.length > 0 && userData) {
          checkAndAwardBadges(skills, userData);
      } else if (userData && skills.length === 0) {
           // Handle case where skills are loaded but empty
           checkAndAwardBadges(skills, userData);
      }
  }, [skills, userData]); // Trigger when skills or user data change

  const handleAddSkill = async (skillData) => {
    try {
      await addDoc(collection(db, 'skills'), {
        ...skillData,
        userId: user.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
    } catch (error) {
      console.error('Error adding skill:', error)
    }
  }

  const handleUpdateSkill = async (skillId, skillData) => {
    try {
      await updateDoc(doc(db, 'skills', skillId), {
        ...skillData,
        updatedAt: new Date().toISOString()
      })
    } catch (error) {
      console.error('Error updating skill:', error)
    }
  }

  const handleDeleteSkill = async (skillId) => {
    try {
      await deleteDoc(doc(db, 'skills', skillId))
    } catch (error) {
      console.error('Error deleting skill:', error)
    }
  }

  const handleEditSkill = (skill, options = {}) => {
    if (options.skipModal) {
      // If skipModal is true, just update the skill in Firestore without showing the modal
      const skillRef = doc(db, 'skills', skill.id);
      updateDoc(skillRef, skill)
        .then(() => {
          // Update local state
          setSkills(prevSkills => 
            prevSkills.map(s => s.id === skill.id ? skill : s)
          );
        })
        .catch(error => {
          console.error('Error updating skill:', error);
          toast.error('Failed to update skill');
        });
    } else {
      // Show the edit modal for regular edits
      setEditingSkill(skill);
      setIsModalOpen(true);
    }
  };

  const handleAddSubtopic = (skill) => {
    setSelectedSkillForSubtopic(skill)
    setShowAddSubtopicModal(true)
  }

  const handleCloseAddSubtopicModal = () => {
    setShowAddSubtopicModal(false)
    setSelectedSkillForSubtopic(null)
  }

  // Handle opening NotesModal
  const handleOpenNotes = (skill, subtopic) => {
    setSelectedSkillForNotes(skill);
    setSelectedSubtopicForNotes(subtopic);
    setShowNotesModal(true);
  };

  // Handle closing NotesModal
  const handleCloseNotes = () => {
    setShowNotesModal(false);
    setSelectedSkillForNotes(null);
    setSelectedSubtopicForNotes(null);
  };

  // Handle opening EditSubtopicModal
  const handleOpenEditSubtopicModal = (skill, subtopic) => {
    setSelectedSkillForEdit(skill);
    setSelectedSubtopicForEdit(subtopic);
    setShowEditSubtopicModal(true);
  };

  // Handle closing EditSubtopicModal
  const handleCloseEditSubtopicModal = () => {
    setShowEditSubtopicModal(false);
    setSelectedSkillForEdit(null);
    setSelectedSubtopicForEdit(null);
  };

  return (
    <div className="w-full max-w-7xl mx-auto space-y-8 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Skills</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Track your learning progress and achievements
          </p>
        </div>
        <button
          onClick={() => {
            setEditingSkill(null)
            setIsModalOpen(true)
          }}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
        >
          Add Skill
        </button>
      </div>

      {/* Section to display earned badges */}
      <div className="space-y-4">
          <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Earned Badges</h2>
              <Link to="/badges" className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
                  View all badges
              </Link>
          </div>
          <div className="flex flex-wrap gap-4">
              {userBadges.length === 0 ? (
                  <p className="text-gray-600 dark:text-gray-400">No badges earned yet. Start learning to earn your first badge!</p>
              ) : (
                  userBadges.map(badgeId => {
                      const badge = badgeCriteria.find(b => b.id === badgeId);
                      if (!badge) return null; // Don't render if badge not found
                      return (
                          <span key={badge.id} className="badge badge-primary px-3 py-1">
                              {badge.name}
                          </span>
                      );
                  })
              )}
          </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {skills.map(skill => (
          <SkillCard
            key={skill.id}
            skill={skill}
            onEdit={handleEditSkill}
            onAddSubtopic={handleAddSubtopic}
            onDelete={handleDeleteSkill}
            onOpenNotes={handleOpenNotes}
            onOpenEditSubtopic={handleOpenEditSubtopicModal}
          />
        ))}
      </div>

      <AddSkillModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setEditingSkill(null)
        }}
        onSave={editingSkill ? handleUpdateSkill : handleAddSkill}
        skill={editingSkill}
      />

      <AddSubtopicModal
        isOpen={showAddSubtopicModal}
        onClose={handleCloseAddSubtopicModal}
        skill={selectedSkillForSubtopic}
      />
      
      <NotesModal
        isOpen={showNotesModal}
        onClose={handleCloseNotes}
        skill={selectedSkillForNotes}
        subtopic={selectedSubtopicForNotes}
      />

      {/* Edit Subtopic Modal */}
      <EditSubtopicModal
        isOpen={showEditSubtopicModal}
        onClose={handleCloseEditSubtopicModal}
        skill={selectedSkillForEdit}
        subtopic={selectedSubtopicForEdit}
      />
    </div>
  )
} 